import { create } from 'zustand';
import { resolveErrorKey } from '@/infrastructure/util/error';
import type { CourseProgress } from '@/domain/model/CourseProgress';
import { CourseProgressService } from '@/application/service/CourseProgressService';
import { CourseProgressApiAdapter } from '@/infrastructure/api/CourseProgressApiAdapter';

export type CourseProgressStatus = 'idle' | 'loading' | 'ready' | 'failed';

interface CourseProgressState {
  progressByCourseId: Record<string, CourseProgress>;
  status: CourseProgressStatus;
  error: string | null;
}

interface CourseProgressActions {
  fetchProgress: (chapterIds: string[]) => Promise<void>;
  saveProgress: (courseId: string, progressPercentage: number) => Promise<void>;
  clearProgress: () => void;
}

const courseProgressService = new CourseProgressService(new CourseProgressApiAdapter());

const indexByCourseId = (progressList: CourseProgress[]): Record<string, CourseProgress> =>
  Object.fromEntries(progressList.map((progress) => [progress.courseId, progress]));

export const useCourseProgressStore = create<CourseProgressState & CourseProgressActions>(
  (set, get) => ({
    progressByCourseId: {},
    status: 'idle',
    error: null,

    fetchProgress: async (chapterIds) => {
      set({ status: 'loading', error: null });
      try {
        const progressList = await courseProgressService.getProgressForChapters(chapterIds);
        set({ progressByCourseId: indexByCourseId(progressList), status: 'ready' });
      } catch (e) {
        set({
          progressByCourseId: {},
          status: 'failed',
          error: resolveErrorKey(e, 'coursePlayer.progressLoadFailed'),
        });
      }
    },

    saveProgress: async (courseId, progressPercentage) => {
      try {
        const progress = await courseProgressService.saveCourseProgress(
          courseId,
          progressPercentage,
        );
        set({
          progressByCourseId: { ...get().progressByCourseId, [courseId]: progress },
          error: null,
        });
      } catch (e) {
        set({ error: resolveErrorKey(e, 'coursePlayer.progressSaveFailed') });
      }
    },

    clearProgress: () => set({ progressByCourseId: {}, status: 'idle', error: null }),
  }),
);
